import {
  toWSDateString,
  WSCard,
  WSGrid,
  WSInfoBox,
  WSPage,
  WSPageToolbar,
  WSText,
  WSTimeline
} from "@wingspanhq/fe-component-library";
import { Batch } from "@wingspanhq/wingspan-api-sdk";
import { queriesBatches } from "@query";
import { useMemo } from "react";

import { WSQueries } from "../../../../query/WSQuery";
import { useBrowserPageTitle } from "../../../../shared/hooks/useBrowserPageTitle";
import {
  routeHelpersImportPayables,
  routeHelpersImportPayablesBatch,
  routeHelpersImportPayablesBatchHistory
} from "../../paths";
import { selectorPayableImportBatchName } from "../../selectors/selectorPayableImportBatchName";

/**
 * v1 recorded a batch event log with an actor per entry; v3 `Batch.events` is a
 * set of timestamps only, so the timeline shows what happened and when but no
 * longer who triggered it.
 */
const getTimelineItems = (batch: Batch) => {
  const entries: Array<{ date?: Date | null; text: string }> = [
    { date: batch.events?.createdAt, text: "Batch created" },
    { date: batch.events?.pendingAt, text: "Import queued" },
    { date: batch.events?.processingAt, text: "Import started" },
    { date: batch.events?.completedAt, text: "Import completed" },
    { date: batch.events?.failedAt, text: "Import failed" }
  ];

  return entries
    .filter((entry): entry is { date: Date; text: string } => !!entry.date)
    .sort((a, b) => a.date.getTime() - b.date.getTime())
    .map(entry => ({
      date: entry.date,
      content: entry.text
    }));
};

export const RouteImportPayableBatchEventHistory: React.FC = () => {
  useBrowserPageTitle("Bulk imports: Payable batch history");
  const { params } = routeHelpersImportPayablesBatchHistory.useData();
  const queryBatch = queriesBatches.getBatch.useQuery({
    batchId: params.batchId
  });
  const goToPayableBatches = routeHelpersImportPayables.useGoTo();
  const goToBatchDetails = routeHelpersImportPayablesBatch.useGoTo();

  const timelineItems = useMemo(
    () => (queryBatch.data ? getTimelineItems(queryBatch.data) : []),
    [queryBatch.data]
  );

  return (
    <WSQueries queries={{ queryBatch }}>
      {({ queryBatchData: batch }) => (
        <WSPage>
          <WSPageToolbar
            mb="XL"
            breadcrumbs={[
              {
                label: "Bulk imports (payables)",
                onClick: () => goToPayableBatches()
              },
              {
                label: selectorPayableImportBatchName(batch),
                onClick: () => goToBatchDetails({ batchId: params.batchId })
              },
              { label: "Batch history" }
            ]}
          />

          <WSText.Heading3 weight="medium" color="gray700" mb="2XL">
            Batch history
          </WSText.Heading3>

          <WSGrid>
            <WSGrid.Item span={{ s: "12", m: "8" }}>
              <WSCard>
                {timelineItems.length > 0 ? (
                  <WSTimeline items={timelineItems} dateFormat="default" />
                ) : (
                  <WSText.ParagraphSm color="gray500">
                    No history events available for this batch yet.
                  </WSText.ParagraphSm>
                )}
              </WSCard>
            </WSGrid.Item>
            <WSGrid.Item span={{ s: "12", m: "4" }}>
              <WSInfoBox>
                v3 records batch milestones as timestamps, so this history shows
                what happened and when, but not which team member triggered it.
              </WSInfoBox>
            </WSGrid.Item>
          </WSGrid>
        </WSPage>
      )}
    </WSQueries>
  );
};
